import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { BaseProvider } from '../../../data/models/provider.model';
import {
  GenericDocument,
  ProviderDocument,
} from '../../../data/models/document.model';
import * as providerSlice from '../../../data/provider.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { GenericDocumentType } from '../../../data/models/document-type.model';
import documentUtils from '../../../services/documentUtils';
import DocumentList from '../DocumentList';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';

interface ProviderDocumentsProps {
  provider: BaseProvider | null;
  uploadingDocument: boolean;
  readOnly: boolean;
  allowView: boolean;
}

export default function ProviderDocuments({
  provider,
  uploadingDocument,
  readOnly,
  allowView,
}: ProviderDocumentsProps) {
  const dispatch = useAppDispatch();

  const providerDocuments = useAppSelector(
    providerSlice.selectProviderDocuments,
  );
  const loadingProviderDocuments = useAppSelector(
    providerSlice.selectLoadingProviderDocuments,
  );
  const providerDocumentTypes = useAppSelector(
    referenceSlice.selectProviderDocumentTypes,
  );

  const [documents, setDocuments] = React.useState<GenericDocument[]>([]);
  const [documentTypes, setDocumentTypes] = React.useState<
    GenericDocumentType[]
  >([]);

  React.useEffect(() => {
    if (provider && provider.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderDocumentsAsync(
          provider.transportationProviderID,
        ),
      );
    }
  }, [provider, dispatch]);

  React.useEffect(() => {
    if (provider && providerDocuments && providerDocumentTypes) {
      const docTypes = documentUtils.providerTypesToGenericTypes(
        providerDocumentTypes,
      );
      const docs = documentUtils.providerToGeneric(
        providerDocuments,
        providerDocumentTypes,
      );
      setDocuments(docs);
      setDocumentTypes(docTypes);
    }
  }, [provider, providerDocuments, providerDocumentTypes]);

  const saveProviderDocument = (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (provider && provider.transportationProviderID) {
      const doc: ProviderDocument = {
        transportationProviderDocumentTypeID: typeId,
        transportationProviderDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(
        providerSlice.uploadProviderDocumentAsync(
          provider.transportationProviderID,
          file,
          doc,
        ),
      );
    }
  };

  const updateProviderDocument = (
    documentId: number,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (provider && provider.transportationProviderID) {
      const doc: ProviderDocument = {
        transportationProviderDocumentID: documentId,
        transportationProviderDocumentTypeID: typeId,
        transportationProviderDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(
        providerSlice.updateProviderDocumentAsync(
          provider.transportationProviderID,
          doc,
        ),
      );
    }
  };

  const deleteProviderDocument = (id: number) => {
    if (provider && provider.transportationProviderID) {
      dispatch(
        providerSlice.deleteProviderDocumentAsync(
          provider.transportationProviderID,
          id,
        ),
      );
    }
  };

  return (
    <DocumentList
      providerID={provider?.transportationProviderID!}
      documents={documents}
      documentTypes={documentTypes}
      uploadingDocument={uploadingDocument || loadingProviderDocuments}
      readOnly={readOnly}
      allowView={allowView}
      save={saveProviderDocument}
      update={updateProviderDocument}
      doDelete={deleteProviderDocument}
    />
  );
}
